import { DateFormatter, getMaxDateForAge } from '@repo/toolkit/date'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { MIN_AGE_REQUIRED } from '@repo/toolkit/min-age-required'
import { TFunction } from 'i18next'
import { z, ZodType } from 'zod'

import { DocumentStatusEnum } from '../../../../lib/types/enums/common/document-status.enum'
import { GenderEnum } from '../../../../lib/types/enums/common/gender.enum'
import { IDTypeEnum } from '../../../../lib/types/enums/common/id-type.enum'
import { LanguageEnum } from '../../../../lib/types/enums/languages/language.enum'
import { LanguageLevelEnum } from '../../../../lib/types/enums/languages/language-level.enum'
import { AcademicZoneEnum } from '../../../../lib/types/enums/staff/academic-zone.enum'
import { StaffPositionEnum } from '../../../../lib/types/enums/staff/staff-position.enum'
import {
  isStaffEnglish,
  StaffQualificationEnum
} from '../../../../lib/types/enums/staff/staff-qualifications.enum'
import { DiplomaEnum } from '../../../../lib/types/enums/staff-application/diploma.enum'
import { EducationLevelEnum } from '../../../../lib/types/enums/staff-application/education-level.enum'
import { EvalutationEnum } from '../../../../lib/types/enums/staff-application/evalutation.enum'
import { MaritalStatusEnum } from '../../../../lib/types/enums/staff-application/marital-status.enum'
import { SchoolLevelTaughtEnum } from '../../../../lib/types/enums/staff-application/school-level-taught.enum'
import { CountryKey } from '../../../../lib/types/models/country/country-list'
import { AdminStaffInput } from '../../../../lib/types/models/staff/admin-staff-input'
import { NullableTransformer } from '../../../../lib/types/utils'
import {
  createDateSchema,
  createDateStringSchema
} from '../../../../lib/utils/validations/date.validation'
import {
  createEnumArraySchema,
  createEnumSchema
} from '../../../../lib/utils/validations/enum-validation'
import { FILE_VALIDATION } from '../../../../lib/utils/validations/file.validation'
import { ibanValidation } from '../../../../lib/utils/validations/iban-validation'
import {
  createNumberArraySchema,
  createNumberSchema
} from '../../../../lib/utils/validations/int-array-validation'
import { PHONE_NUMBER_SCHEMA } from '../../../../lib/utils/validations/phone-number.validation'
import { createStringSchema } from '../../../../lib/utils/validations/string-validation'

export const staffFormSchema = (
  language: AvailableLanguages,
  t: TFunction<AvailableLanguages, undefined>
): ZodType<NullableTransformer<AdminStaffInput>> =>
  z
    .object({
      picture: FILE_VALIDATION().nullish(),
      gender: createEnumSchema({ fieldName: 'genre', enumType: GenderEnum }),
      firstName: createStringSchema('prénom'),
      lastName: createStringSchema('NOM'),
      maidenName: createStringSchema('', { nullish: true }),
      phoneNumber: PHONE_NUMBER_SCHEMA(t),
      email: createStringSchema('adresse email', { isEmail: true }),
      address1: createStringSchema('adresse'),
      address2: createStringSchema('complément d’adresse', { nullish: true }),
      city: createStringSchema('ville'),
      postalCode: createStringSchema('code postal'),
      country: createStringSchema('pays'),
      academicZone: createEnumSchema({
        fieldName: 'zone académique',
        enumType: AcademicZoneEnum,
        nullish: true
      }),
      birthDate: createDateStringSchema({
        inputName: 'date de naissance',
        maxDate: DateFormatter.toDateFormatted(getMaxDateForAge(MIN_AGE_REQUIRED)),
        errorMessage: `Âge minimum requis: ${MIN_AGE_REQUIRED} ans`
      }),
      recruitedAt: createDateStringSchema({ inputName: 'date de recrutement' }).nullish(),
      recruitedById: createNumberSchema('recruté par', { nullish: true }),
      birthPlace: createEnumSchema({ fieldName: 'lieu de naissance', enumType: CountryKey }),
      birthDepartment: createStringSchema('département de naissance', { nullish: true }),
      birthCity: createStringSchema('ville de naissance', { nullish: true }),
      nationality: createStringSchema('nationalité'),
      socialSecurityNumber: createStringSchema('numéro de sécurité sociale', { nullish: true }),
      allergies: createStringSchema('allergies', { nullish: true }),
      maritalStatus: createEnumSchema({
        fieldName: 'statut marital',
        enumType: MaritalStatusEnum
      }).nullish(),
      numberOfChildren: createNumberSchema("nombre d'enfants", {
        isInteger: true,
        nullish: true
      }),
      eta: FILE_VALIDATION().nullish(),
      etaStatus: createEnumSchema({
        fieldName: 'statut',
        enumType: DocumentStatusEnum,
        nullish: true
      }),
      position: createEnumSchema({ fieldName: 'poste', enumType: StaffPositionEnum }),
      qualifications: createEnumArraySchema({
        fieldName: 'qualifications',
        enumType: StaffQualificationEnum,
        minimumNumberOfItems: 1
      }),
      currentSituation: createStringSchema('situation actuelle', { nullish: true }),
      educationLevel: createEnumSchema({
        fieldName: "Niveau d'étude",
        enumType: EducationLevelEnum,
        nullish: true
      }),
      studiedDomain: createStringSchema('domaine d’étude', { nullish: true }),
      establishmentName: createStringSchema('NOM de l’établissement', { nullish: true }),
      establishmentAddress: createStringSchema('adresse de l’établissement', { nullish: true }),
      establishmentPostalCode: createStringSchema('code postal de l’établissement', {
        nullish: true
      }),
      establishmentCity: createStringSchema('ville de l’établissement', { nullish: true }),
      idStatus: createEnumSchema({
        fieldName: 'Statut',
        enumType: DocumentStatusEnum,
        nullish: true
      }),
      idType: createEnumSchema({
        fieldName: "type de pièce d'identité",
        enumType: IDTypeEnum,
        nullish: true
      }),
      idNumber: createStringSchema('numéro de pièce d’identité', { nullish: true }),
      idFile: FILE_VALIDATION().nullish(),
      idDeliveredBy: createStringSchema('délivré par', { nullish: true }),
      idDeliveredAt: createDateStringSchema({ inputName: 'date de délivrance' }).nullish(),
      idValidUntil: createDateStringSchema({ inputName: "date d'expiration" }).nullish(),
      criminalRecordStatus: createEnumSchema({
        fieldName: 'statut',
        enumType: DocumentStatusEnum
      }),
      criminalRecord: FILE_VALIDATION().nullish(),
      paySlipStatus: createEnumSchema({
        fieldName: 'Statut',
        enumType: DocumentStatusEnum,
        nullish: true
      }),
      paySlip: FILE_VALIDATION().nullish(),
      iban: ibanValidation(language).nullish(),
      bic: createStringSchema('BIC', { nullish: true }),
      hasComplemenaryInsurance: z.boolean(),
      partOfTheEuropeanUnion: z.boolean({
        message: 'Veuillez choisir une option'
      }),
      complementaryInsuranceName: createStringSchema('NOM de l’assurance complémentaire', {
        nullish: true
      }),
      emergencyContactName: createStringSchema('NOM', { nullish: true }),
      emergencyContactPhoneNumber: PHONE_NUMBER_SCHEMA(t).nullish(),
      emergencyContactRelationship: createStringSchema('lien de parenté', { nullish: true }),
      isAvailableForTheYear: z.boolean({ required_error: 'Veuillez choisir une disponibilité' }),
      hobbyIds: createNumberArraySchema('loisirs'),
      formulasWishListIds: createNumberArraySchema('formules').nullish(),
      countriesWishListIds: createNumberArraySchema('pays').nullish(),
      howDidYouHearAboutUsOptionId: createNumberSchema(
        'Comment avez-vous entendu parler de nous',
        {
          nullish: true
        }
      ),
      classLevels: createEnumArraySchema({ fieldName: 'niveau', enumType: SchoolLevelTaughtEnum }),
      globalEvaluation: createEnumSchema({
        fieldName: 'appréciation globale',
        enumType: EvalutationEnum,
        nullish: true
      }),
      diplomas: z.array(
        z.object({
          status: createEnumSchema({ fieldName: 'statut', enumType: DocumentStatusEnum }),
          type: createEnumSchema({ fieldName: 'type', enumType: DiplomaEnum }),
          file: FILE_VALIDATION().nullish(),
          diplomaTitle: createStringSchema('NOM du diplôme', { nullish: true })
        })
      ),
      availabilities: z.array(
        z.object({
          startDate: createDateStringSchema({ inputName: 'date de début' }),
          endDate: createDateStringSchema({ inputName: 'date de fin' })
        })
      ),
      languages: z.array(
        z.object({
          language: createEnumSchema({ fieldName: 'langue', enumType: LanguageEnum }),
          level: createEnumSchema({ fieldName: 'niveau', enumType: LanguageLevelEnum })
        })
      ),
      comments: z.array(
        z.object({
          id: z.number().nullish(),
          subject: z
            .object({
              id: z.number(),
              name: createStringSchema('sujet')
            })
            .nullish(),
          comment: createStringSchema('commentaire'),
          commentedByName: createStringSchema('commenté par'),
          date: createDateSchema({ inputName: 'date et heure' }),
          displayForDirector: z.boolean(),
          displayInPlanning: z.boolean()
        })
      )
    })
    .superRefine((data, ctx) => {
      if (!isStaffEnglish(data)) {
        if (!data.socialSecurityNumber) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('errors.string.required', { fieldName: 'numéro de sécurité sociale' }),
            path: ['socialSecurityNumber']
          })
        }
        if (!data.academicZone) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('errors.enum.required', { fieldName: 'zone académique' }),
            path: ['academicZone']
          })
        }
      }

      const otherDiplomas = data.diplomas.filter((diploma) => diploma.type === DiplomaEnum.OTHER)
      for (const otherDiploma of otherDiplomas) {
        if (otherDiploma && !otherDiploma.diplomaTitle) {
          const index = data.diplomas.findIndex((diploma) => otherDiploma === diploma)
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Veuillez entrer un NOM de diplôme',
            path: ['diplomas', index, 'otherDiplomaName']
          })
        }
      }

      if (data.availabilities.length > 0) {
        const availabilities = data.availabilities
        availabilities.forEach((availability, index) => {
          if (
            availability.startDate &&
            availability.endDate &&
            availability.startDate > availability.endDate
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'La date de début doit être antérieure à la date de fin',
              path: ['availabilities', index, 'startDate']
            })
          }
        })
      }
    })
